import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { recommendationService } from "../../api/recommendations";
import jobPostingService from "../../api/jobPosting";
import Swal from "sweetalert2";
import {
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Share2,
  ArrowRight,
} from "lucide-react";

const ProfessionalRecommendationsPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRec, setSelectedRec] = useState(null);
  const [viewedRecs, setViewedRecs] = useState(new Set());

  // Fetch recommendations received by professional
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const data = await recommendationService.getReceived();
        // console.log(data);
        setRecommendations(data.results || data);
        setError("");
      } catch (err) {
        setError("Failed to load recommendations");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, []);

  // Mark all recommendations as viewed when they are loaded
  useEffect(() => {
    if (recommendations.length > 0) {
      const markAllViewed = async () => {
        try {
          const unviewed = recommendations.filter(rec => !viewedRecs.has(rec.id));
          if (unviewed.length === 0) return;
          
          await Promise.all(
            unviewed.map(rec => recommendationService.markViewed(rec.id))
          );
          
          setViewedRecs(prev => {
            const newSet = new Set(prev);
            unviewed.forEach(rec => newSet.add(rec.id));
            return newSet;
          });
        } catch (err) {
          console.error("Error marking as viewed:", err);
        }
      };
      markAllViewed();
    }
  }, [recommendations, viewedRecs]);

  // Open job details
  const handleViewJob = async (rec) => {
    try {
      await recommendationService.markClicked(rec.id);
      // Open job details
      navigate(`/job-details/${rec.job.id}`);
    } catch (err) {
      console.error("Error marking as clicked:", err);
      navigate(`/job-details/${rec.job.id}`);
    }
  };

  // Share externally
  const handleShare = async (rec, channel) => {
    const channelNames = {
      whatsapp: "WhatsApp",
      linkedin: "LinkedIn",
      twitter: "X (Twitter)",
      copy: "Clipboard",
    };

    const confirmResult = await Swal.fire({
      title: "Confirm Share",
      text: `Share this job opportunity via ${channelNames[channel]}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, share it!"
    });

    if (!confirmResult.isConfirmed) return;

    try {
      await recommendationService.markShared(rec.id);
    } catch (err) {
      console.error("Error marking as shared:", err);
    }

    const baseUrl = window.location.origin;
    // Professionals share the direct job link since they don't generate trackable share tokens
    const shareUrl = `${baseUrl}/job-details/${rec.job.id}`;

    const messages = {
      whatsapp: `Check out this job: ${rec.job.title}\n\n${shareUrl}`,
      linkedin: `I found this great opportunity: ${rec.job.title}\n\n${shareUrl}`,
      twitter: `Great job opportunity: ${rec.job.title} 🎯\n\n${shareUrl}`,
    };

    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(messages.whatsapp)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(messages.twitter)}`,
    };

    if (channel === "copy") {
      navigator.clipboard.writeText(shareUrl);
      Swal.fire({
        title: "Copied!",
        text: "Share link copied to clipboard.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });
    } else {
      window.open(urls[channel], "_blank");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600">Loading recommendations...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Job Recommendations
          </h1>
          <p className="text-gray-600 mt-2">
            Recommended opportunities from recruiters
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {recommendations.length === 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-600 mb-4">
              You don't have any recommendations yet
            </p>
            <p className="text-sm text-gray-500">
              When recruiters find a good fit, they'll send you recommendations
              here
            </p>
          </div>
        )}

        {/* Recommendations Grid */}
        {recommendations.length > 0 && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recommendations.map((rec) => (
              <div
                key={rec.id}
                className="bg-white rounded-lg shadow-md hover:shadow-lg transition overflow-hidden border border-gray-200"
              >
                {/* Job Header */}
                <div className="p-4 border-b border-gray-200">
                  <h3 className="font-semibold text-lg line-clamp-2">
                    {rec.job.title}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1"> {rec.job.location}</p>
                </div>

                {/* Recruiter Info */}
                <div className="px-4 py-3 bg-blue-50">
                  <p className="text-xs text-gray-600">Recommended by</p>
                  <p className="font-medium text-sm">
                    {rec.recruiter.first_name || rec.recruiter.email}
                  </p>
                </div>

                {/* Message */}
                <div className="p-4">
                  <p className="text-sm text-gray-700 italic line-clamp-3">
                    "{rec.message}"
                  </p>
                </div>

                {/* Metadata */}
                <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600">
                  Sent: {new Date(rec.created_at).toLocaleDateString()}
                </div>

                {/* Actions */}
                <div className="p-4 space-y-2 border-t border-gray-200">
                  <button
                    onClick={() => handleViewJob(rec)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2 text-sm font-medium"
                  >
                    View Opportunity
                    <ExternalLink size={16} />
                  </button>

                  <details className="w-full">
                    <summary className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition cursor-pointer text-sm font-medium flex items-center justify-center gap-2">
                      <Share2 size={16} />
                      Share
                    </summary>
                    <div className="mt-2 space-y-2">
                      <button
                        onClick={() => handleShare(rec, "whatsapp")}
                        className="w-full px-4 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition"
                      >
                        WhatsApp
                      </button>
                      <button
                        onClick={() => handleShare(rec, "linkedin")}
                        className="w-full px-4 py-2 bg-blue-700 text-white text-sm rounded hover:bg-blue-800 transition"
                      >
                        LinkedIn
                      </button>
                      <button
                        onClick={() => handleShare(rec, "twitter")}
                        className="w-full px-4 py-2 bg-black text-white text-sm rounded hover:bg-gray-800 transition"
                      >
                        X (Twitter)
                      </button>
                      <button
                        onClick={() => handleShare(rec, "copy")}
                        className="w-full px-4 py-2 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition"
                      >
                        Copy Link
                      </button>
                    </div>
                  </details>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalRecommendationsPage;
